import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus, Check } from "lucide-react";
import { SectionLabel } from "./sharedUI";

const PACKAGES = [
  {
    id: "start", name: "Start", price: 49,
    best: "For newly self-employed tradespeople.",
    keyPoints: ["One-page website", "Simple logo & brand", "Domain + hosting", "Quotes & invoices"],
    cta: "Get Started",
  },
  {
    id: "pro", name: "Pro", price: 69, popular: true,
    best: "For growing trade businesses.",
    keyPoints: ["Up to five pages", "Full brand identity", "Google Business setup", "Convert quotes → invoices", "Automatic reminders"],
    cta: "Choose Pro",
  },
  {
    id: "growth", name: "Growth", price: 99,
    best: "For established trade businesses.",
    keyPoints: ["Everything in Pro", "Up to ten pages", "Location pages", "Analytics + Search Console", "Priority support"],
    cta: "Grow My Business",
  },
];

export const PricingSection = () => {
  const [expanded, setExpanded] = useState({});

  return (
    <section id="pricing" className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]" data-testid="pricing-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionLabel n="04" name="Pricing / Monthly" />

        <div className="grid grid-cols-12 gap-6 items-baseline mb-14">
          <div className="col-span-12 lg:col-span-7">
            <h2 className="h-lg text-[color:var(--ink)]">
              One monthly payment. <span className="font-serif-italic">No large upfront cost.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[15px] leading-[1.6] text-[color:var(--muted)] max-w-md">
              Every plan includes design, build, hosting, maintenance and small ongoing updates. No setup fee. 12-month minimum term.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {PACKAGES.map((pkg, index) => {
            const isOpen = expanded[pkg.id];
            const shown = isOpen ? pkg.keyPoints : pkg.keyPoints.slice(0, 4);
            return (
              <article
                key={pkg.id}
                className={`relative rounded-sm border p-6 lg:p-8 flex flex-col ${
                  pkg.popular
                    ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white"
                    : "border-[color:var(--border)] bg-[color:var(--bg-paper)] text-[color:var(--ink)]"
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className={`grid-ref ${pkg.popular ? "text-white/60" : ""}`}>0{index + 1} / Plan</span>
                  {pkg.popular ? (
                    <span className="stamp stamp-accent" style={{ background: 'rgba(239, 91, 37, 0.15)' }}>Most Popular</span>
                  ) : (
                    <span className={`stamp ${pkg.popular ? "stamp-light" : "stamp-ink"}`}>Available</span>
                  )}
                </div>

                <p className="font-display leading-none" style={{ fontSize: 'clamp(1.75rem, 1.5vw + 1rem, 2.25rem)' }}>
                  {pkg.name}
                </p>
                <p className={`text-[14px] mt-2 leading-[1.5] ${pkg.popular ? "text-white/70" : "text-[color:var(--muted)]"}`}>
                  {pkg.best}
                </p>

                <div className={`h-px my-6 ${pkg.popular ? "bg-white/15" : "bg-[color:var(--border-soft)]"}`} />

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display leading-none tabular" style={{ fontSize: 'clamp(3rem, 3vw + 2rem, 4rem)' }}>£{pkg.price}</span>
                  <span className={`text-[14px] ${pkg.popular ? "text-white/70" : "text-[color:var(--muted)]"}`}>/ month</span>
                </div>

                <ul className="space-y-2.5 flex-1">
                  {shown.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[14px] leading-snug">
                      <Check size={14} strokeWidth={2} className={`mt-0.5 flex-shrink-0 ${pkg.popular ? "text-[color:var(--accent)]" : "text-[color:var(--ink)]"}`} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                {pkg.keyPoints.length > 4 && (
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [pkg.id]: !p[pkg.id] }))}
                    className={`mt-4 text-[12px] font-semibold uppercase tracking-widest inline-flex items-center gap-1.5 ${
                      pkg.popular ? "text-white/80 hover:text-white" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                    }`}
                    data-testid={`pricing-toggle-${pkg.id}`}
                  >
                    {isOpen ? <><Minus size={12} /> Show less</> : <><Plus size={12} /> More features</>}
                  </button>
                )}

                <Link
                  to={`/get-started?package=${pkg.id}`}
                  className={`mt-8 w-full text-center rounded-full px-4 py-3 text-[13px] font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? "bg-[color:var(--accent)] text-white hover:brightness-110"
                      : "bg-[color:var(--ink)] text-white hover:brightness-125"
                  }`}
                  data-testid={`pricing-cta-${pkg.id}`}
                >
                  {pkg.cta} <ArrowRight size={14} />
                </Link>

                <p className={`mt-4 text-[11px] ${pkg.popular ? "text-white/50" : "text-[color:var(--muted)]"}`}>
                  No setup fee. 12-month minimum term.
                </p>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-[color:var(--border-soft)] pt-6">
          <p className="grid-ref">Full comparison table on /pricing</p>
          <Link to="/pricing" className="btn-ghost text-[14px]" data-testid="see-full-pricing">
            Compare every feature <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};
